"use client";

import { Facebook, Instagram, MapPin, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FooterNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-3">
        {[0, 1, 2, 3, 4].map(i => <li key={i} data-index={i} className="h-5 w-24 bg-muted animate-pulse rounded-md" />)}
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="group relative inline-flex items-center text-foreground font-default font-medium text-[0.95rem] transition-colors duration-200 hover:text-primary">
            <span className="relative">
              {item.label}
              <span className="absolute left-0 -bottom-0.5 h-[1px] w-0 bg-primary transition-all duration-[260ms] ease-out group-hover:w-full" />
            </span>
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const socials = [{
    name: "Instagram",
    href: "https://instagram.com/hardrockcytattoo",
    Icon: Instagram
  }, {
    name: "Facebook",
    href: "https://facebook.com/p/Hard-Rock-tattoo-100043242321363",
    Icon: Facebook
  }];
  const routeStops = [{
    label: "Paphos"
  }];
  return <section data-section-id="1358" id="footer" className="bg-background text-foreground relative overflow-hidden border-t border-border">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-20 h-64 w-64 rounded-md bg-primary/10 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 right-10 h-72 w-72 rounded-md bg-secondary/20 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 right-1/3 h-40 w-40 rounded-md bg-primary/5 blur-3xl" />

      <div className="relative max-w-[1280px] mx-auto px-5 md:px-8">
        {}
        <div className="pt-12 md:pt-16">
          <div className="flex items-center gap-4 md:gap-6 overflow-hidden">
            {routeStops.map((stop, i) => <div data-index={i} key={stop.label} className="flex items-center gap-4 md:gap-6 flex-1">
                <div className="flex items-center gap-2 shrink-0">
                  <span className="relative inline-flex items-center justify-center">
                    <span className="absolute inline-flex h-[14px] w-[14px] rounded-md border border-primary" />
                    <span className="relative inline-flex h-[6px] w-[6px] rounded-sm bg-primary" />
                  </span>
                  <span className="font-mono uppercase tracking-[0.2em] text-[0.7rem] md:text-[0.75rem] text-foreground/60">
                    {stop.label}
                  </span>
                </div>
                {i < routeStops.length - 1 && <div aria-hidden="true" className="flex-1 h-px border-t border-border" />}
              </div>)}
          </div>
        </div>

        {}
        <div ref={ref} className={`py-20 md:py-24 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
            {}
            <div className="md:col-span-4 flex flex-col gap-5">
              <Link to="/" className="inline-flex items-center">
                <WvcLogo className="h-12 w-auto" />
              </Link>
              <p className="font-default text-[1.1rem] leading-[1.6] text-foreground/80 max-w-xs">
                Where Ink Becomes Legend.
                <br />
                Paphos, Cyprus.
              </p>
              <div className="relative inline-block w-20 h-20 mt-2">
                <div className="absolute inset-0 rounded-md bg-secondary" />
                <div className="absolute inset-1 rounded-md bg-background flex items-center justify-center border border-border">
                  <span className="font-default text-3xl text-primary">H</span>
                </div>
                <div aria-hidden="true" className="absolute -bottom-1 -right-1 h-4 w-4 rounded-sm bg-primary" />
              </div>
            </div>

            {}
            <div className="md:col-span-3 flex flex-col gap-4">
              <span className="inline-block self-start font-mono uppercase tracking-[0.2em] text-[0.7rem] font-bold text-primary-foreground bg-primary px-3 py-1 rounded-md">
                The Studio
              </span>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="font-default text-[1rem] leading-[1.55] text-foreground">
                  Danaes str., Blue Horizon Court
                  <br />
                  Block B, Shop 4, Paphos 8042
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="relative inline-flex items-center justify-center shrink-0">
                  <span className="absolute inline-flex h-[18px] w-[18px] rounded-md border border-primary" />
                  <span className="relative inline-flex h-[8px] w-[8px] rounded-sm bg-primary" />
                </span>
                <Link to="tel:+35794043514" className="font-default text-[1rem] text-foreground hover:text-primary transition-colors duration-200">
                  +357 94 043514
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-primary shrink-0" />
                <Link to="https://wa.me/35794043514" className="font-default text-[1rem] text-foreground hover:text-primary transition-colors duration-200">
                  WhatsApp George
                </Link>
              </div>
            </div>

            {}
            <div className="md:col-span-3 flex flex-col gap-4">
              <span className="inline-block self-start font-mono uppercase tracking-[0.2em] text-[0.7rem] font-bold text-primary-foreground bg-primary px-3 py-1 rounded-md">
                Navigation
              </span>
              <MenuProvider menu_id="header_menu" className="footer-menu">
                <FooterNavConsumer />
              </MenuProvider>
            </div>

            {}
            <div className="md:col-span-2 flex flex-col gap-4">
              <span className="inline-block self-start font-mono uppercase tracking-[0.2em] text-[0.7rem] font-bold text-primary-foreground bg-primary px-3 py-1 rounded-md">
                Socials
              </span>
              <div className="flex flex-wrap gap-3">
                {socials.map((social, i) => {
                const Icon = social.Icon;
                return <Button data-index={i} key={social.name} asChild variant="outline" size="icon" className="h-11 w-11 rounded-md border border-border bg-secondary text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-[220ms] ease-out">
                      <Link to={social.href} aria-label={social.name}>
                        <Icon className="h-5 w-5" />
                      </Link>
                    </Button>;
              })}
              </div>
              <p className="font-default text-[0.85rem] leading-[1.5] text-foreground/60 mt-1">
                Mon–Sat 10am–7pm
                <br />
                Sun 11am–5pm
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="border-t border-border py-6 md:py-7">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="font-mono text-[0.7rem] tracking-[0.1em] text-foreground/50 uppercase">
              Paphos &middot; Cyprus &middot; Artist George &middot; ARTIST DANIELLA
            </p>
          </div>
        </div>
      </div>
    </section>;
}